import { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { ArrowDownAZ, ArrowUpAZ, Search } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  type RowSelectionState,
} from '@tanstack/react-table';

import { DepositAddIntegrationLayout } from 'src/components/Deposit/Add';
import { SimplePagination } from '@/components/ui/simple-pagination';
import { useAddDeposit } from 'src/context/deposit';
import useSorting from 'src/hooks/use-sorting';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from 'src/lib/utils';

import { withInitialOAuthData } from './lib/withInitialOAuthData';

type ITableRow = {
  oauthRepositoryId: string;
  oauthRepositoryFullName: string;
};

type Props = {
  repositories: ITableRow[];
  isLoading: boolean;
};

const columnHelper = createColumnHelper<ITableRow>();

const OAuthDepositContainer = ({ repositories, isLoading }: Props) => {
  const {
    ui: { oauthData, integration, getBack, setOauthData },
    contentType,
  } = useAddDeposit();
  const [currentRepositories, setCurrentRepositories] = useState<ITableRow[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    oauthData.reduce((acc, { oauthRepositoryId }) => ({ ...acc, [oauthRepositoryId]: true }), {}),
  );
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = [
    columnHelper.accessor('oauthRepositoryId', {
      header: ({ table }) => {
        const handleChange = (value: boolean) => table.toggleAllPageRowsSelected(value);

        return (
          <Checkbox checked={table.getIsAllRowsSelected()} onCheckedChange={handleChange} aria-label="Select all" />
        );
      },
      cell: ({ row }) => {
        const handleChange = (value: boolean) => row.toggleSelected(!!value);

        return <Checkbox checked={row.getIsSelected()} onCheckedChange={handleChange} aria-label="Select row" />;
      },
    }),
    columnHelper.accessor('oauthRepositoryFullName', {
      header: 'Name',
      cell: (props) => props.getValue(),
    }),
  ];
  const table = useReactTable({
    data: currentRepositories,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
      pagination,
    },
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    getRowId: (row) => row.oauthRepositoryId,
    getPaginationRowModel: getPaginationRowModel(),
  });
  useEffect(() => {
    if (!isLoading) setCurrentRepositories(repositories);
  }, [repositories, isLoading]);

  const [order, setOrder] = useSorting();

  const onSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const filteredRepos = repositories.filter(({ oauthRepositoryFullName: name }) =>
      name.toLowerCase().includes(e.target.value.toLowerCase()),
    );

    setCurrentRepositories(filteredRepos);
  }, 500);

  const onFilterChange = () => {
    setOrder();
    setCurrentRepositories((repositories) => [...repositories].reverse());
  };

  const onConfirm = () => {
    const selectedRows = table.getSelectedRowModel().rows.map((row) => ({
      ...row.original,
      contentType,
    }));
    setOauthData(selectedRows);
  };

  return (
    <DepositAddIntegrationLayout
      integration={{
        provider: integration?.platform || '',
        account: integration?.name || '',
      }}
      onConfirm={onConfirm}
      confirmButton={{
        disabled: !table.getFilteredSelectedRowModel().rows.length,
      }}
      onBack={getBack}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="w-[373px] max-w-full relative">
          <Input placeholder="Search repositories" className="pl-8" onChange={onSearch} />
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        </div>
        <Button size="icon" variant="secondary" onClick={onFilterChange}>
          {order === 'asc' ? <ArrowUpAZ /> : <ArrowDownAZ />}
        </Button>
      </div>
      <div className="italic text-sm text-foreground mb-4">
        {table.getFilteredSelectedRowModel().rows.length} assets selected
      </div>
      <div className="border border-border rounded-md mb-4 h-[580px]">
        <Table isLoading={isLoading} maxHeight="h-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <TableHead key={header.id} className={cn('font-bold', index === 0 && 'w-10')}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() ? 'selected' : undefined}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center font-bold">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <SimplePagination
        page={table.getState().pagination.pageIndex + 1}
        totalPages={table.getPageCount()}
        totalItems={table.getFilteredRowModel().rows.length}
        limit={table.getState().pagination.pageSize}
        onPrevious={table.previousPage}
        onNext={table.nextPage}
        isPreviousDisabled={!table.getCanPreviousPage()}
        isNextDisabled={!table.getCanNextPage()}
        className="justify-end"
        hideOnSinglePage
      />
    </DepositAddIntegrationLayout>
  );
};

export default withInitialOAuthData(OAuthDepositContainer);
